import React, { useState, useEffect } from 'react';
import './CreateChallengeModal.css';

function CreateChallengeModal({ isOpen, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    visibility: 'my_team',
    participation: 'team',
    challenge_type: 'count_tasks',
    target_number: 10,
    end_time: '',
    prize: '',
    start_mode: 'after_deadline',
    task_types: []
  });
  
  const [availableTasks, setAvailableTasks] = useState({
    lead: [],
    customer: [],
    marketer: [],
    leader: []
  });
  
  const [selectedTasks, setSelectedTasks] = useState([
    { id: 1, stage: '', task_type_id: '', points: 1 }
  ]);
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      fetchTaskTypes();
      // Set default end time to 24 hours from now
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      const dateTimeString = tomorrow.toISOString().slice(0, 16);
      setFormData(prev => ({ ...prev, end_time: dateTimeString }));
      // Reset selected tasks
      setSelectedTasks([{ id: 1, stage: '', task_type_id: '', points: 1 }]);
    }
  }, [isOpen]);

  const fetchTaskTypes = async () => {
    try {
      // Fetch task types for each stage
      const stages = ['lead', 'customer', 'marketer', 'leader'];
      const tasks = {};
      
      for (const stage of stages) {
        const response = await fetch(`/backend/api/admin/stage-tasks.php?stage=${stage}`, {
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          tasks[stage] = data.task_types || [];
        }
      }
      
      setAvailableTasks(tasks);
    } catch (error) {
      console.error('Error fetching task types:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleStageChange = (taskId, stage) => {
    setSelectedTasks(prev => prev.map(task => 
      task.id === taskId 
        ? { ...task, stage, task_type_id: '' } // Reset task when stage changes
        : task
    ));
  };

  const handleTaskChange = (taskId, task_type_id) => {
    setSelectedTasks(prev => prev.map(task => 
      task.id === taskId 
        ? { ...task, task_type_id }
        : task
    ));
  };

  const addNewTask = () => {
    if (selectedTasks.length < 10) {
      const newId = Math.max(...selectedTasks.map(t => t.id)) + 1;
      setSelectedTasks(prev => [...prev, { id: newId, stage: '', task_type_id: '', points: 1 }]);
    }
  };

  const removeTask = (taskId) => {
    if (selectedTasks.length > 1) {
      setSelectedTasks(prev => prev.filter(task => task.id !== taskId));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    // Convert selectedTasks to task_types format
    const taskTypes = selectedTasks
      .filter(task => task.stage && task.task_type_id)
      .map(task => ({
        stage: task.stage,
        task_type_id: parseInt(task.task_type_id),
        points: task.points
      }));
    
    // Validation
    if (!formData.title.trim()) {
      setError('Title is required');
      return;
    }
    
    if (taskTypes.length === 0) {
      setError('Please select at least one task type');
      return;
    }
    
    if (formData.challenge_type === 'count_tasks' && (!formData.target_number || formData.target_number < 1)) {
      setError('Target number must be at least 1');
      return;
    }
    
    if (formData.challenge_type === 'time_based' && !formData.end_time) {
      setError('End time is required for time-based challenges');
      return;
    }
    
    setLoading(true);
    
    try {
      const submitData = { ...formData, task_types: taskTypes };
      const response = await fetch('/backend/api/challenges/create.php', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(submitData)
      });
      
      const data = await response.json();
      
      if (data.success) {
        onSuccess();
        onClose();
        // Reset form
        setFormData({
          title: '',
          description: '',
          visibility: 'my_team',
          participation: 'team',
          challenge_type: 'count_tasks',
          target_number: 10,
          end_time: '',
          prize: '',
          start_mode: 'after_deadline',
          task_types: []
        });
        setSelectedTasks([{ id: 1, stage: '', task_type_id: '', points: 1 }]);
      } else {
        setError(data.error || 'Failed to create challenge');
      }
    } catch (error) {
      console.error('Error creating challenge:', error);
      setError('Failed to create challenge. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const getAcceptanceDeadline = () => {
    const deadline = new Date();
    deadline.setHours(deadline.getHours() + 24);
    return deadline.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="create-challenge-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>🏆 Create Challenge</h2>
          <button className="close-btn" onClick={onClose}>&times;</button>
        </div>
        
        {error && <div className="error-message">{error}</div>}
        
        <form onSubmit={handleSubmit} className="challenge-form">
          <div className="form-group">
            <label htmlFor="title">Challenge Title *</label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleChange}
              placeholder="e.g., Most Presentations This Week"
              required
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Add details about the challenge..."
              rows={3}
            />
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="visibility">Visibility</label>
              <select id="visibility" name="visibility" value={formData.visibility} onChange={handleChange}>
                <option value="my_team">My Team</option>
                <option value="everyone">Everyone</option>
              </select>
            </div>
            
            <div className="form-group">
              <label htmlFor="participation">Who Can Join</label>
              <select id="participation" name="participation" value={formData.participation} onChange={handleChange}>
                <option value="team">Team Members</option>
                <option value="everyone">Everyone</option>
              </select>
            </div>
          </div>
          
          <div className="form-group">
            <label htmlFor="challenge_type">Challenge Type</label>
            <select id="challenge_type" name="challenge_type" value={formData.challenge_type} onChange={handleChange}>
              <option value="count_tasks">First to Reach Target</option>
              <option value="time_based">Most Points by Deadline</option>
            </select>
          </div>
          
          {formData.challenge_type === 'count_tasks' && (
            <div className="form-group">
              <label htmlFor="target_number">Target Number *</label>
              <input
                type="number"
                id="target_number"
                name="target_number"
                value={formData.target_number}
                onChange={handleChange}
                min="1"
                placeholder="10"
                required
              />
            </div>
          )}
          
          {formData.challenge_type === 'time_based' && (
            <div className="form-group">
              <label htmlFor="end_time">End Date & Time *</label>
              <input
                type="datetime-local"
                id="end_time"
                name="end_time"
                value={formData.end_time}
                onChange={handleChange}
                required
              />
            </div>
          )}
          
          <div className="form-group">
            <label htmlFor="prize">Prize (Optional)</label>
            <input
              type="text"
              id="prize"
              name="prize"
              value={formData.prize}
              onChange={handleChange}
              placeholder="e.g., $100 Gift Card"
            />
          </div>
          
          <div className="form-group task-selection">
            <label>Select Tasks That Count *</label>
            <p className="task-selection-hint">Choose which contact tasks will earn points in this challenge</p>
            
            <div className="task-selection-container">
              {selectedTasks.map((task, index) => (
                <div key={task.id} className="task-selection-row">
                  <div className="task-selects">
                    <select 
                      value={task.stage} 
                      onChange={(e) => handleStageChange(task.id, e.target.value)}
                      className="stage-select"
                    >
                      <option value="">Select Stage</option>
                      <option value="lead">Lead</option>
                      <option value="customer">Customer</option>
                      <option value="marketer">Marketer</option>
                      <option value="leader">Leader</option>
                    </select>
                    
                    {task.stage && (
                      <select 
                        value={task.task_type_id} 
                        onChange={(e) => handleTaskChange(task.id, e.target.value)}
                        className="task-select"
                      >
                        <option value="">Select Task</option>
                        {availableTasks[task.stage]?.map(t => (
                          <option key={t.id} value={t.id}>{t.name}</option>
                        ))}
                      </select>
                    )}
                  </div>
                  
                  {selectedTasks.length > 1 && (
                    <button 
                      type="button" 
                      onClick={() => removeTask(task.id)}
                      className="remove-task-btn"
                      title="Remove this task"
                    >
                      ×
                    </button>
                  )}
                </div>
              ))}
              
              {selectedTasks.length < 10 && selectedTasks.some(task => task.stage && task.task_type_id) && (
                <button 
                  type="button" 
                  onClick={addNewTask}
                  className="add-task-btn"
                >
                  + Add Another Task
                </button>
              )}
            </div>
          </div>
          
          <div className="acceptance-info">
            <span className="info-icon">ℹ️</span>
            <span>Participants have 24 hours to join (until {getAcceptanceDeadline()})</span>
          </div>
          
          <div className="form-actions">
            <button type="button" className="cancel-btn" onClick={onClose}>Cancel</button>
            <button type="submit" className="submit-btn" disabled={loading}>
              {loading ? 'Creating...' : 'Create Challenge'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default CreateChallengeModal;

